import { z } from 'zod';

type UploadedFileLike = {
  readonly name: string;
  readonly type: string;
  readonly size: number;
};

function matchesMimeTypes({
  candidate,
  mimeTypes,
}: {
  candidate: UploadedFileLike;
  mimeTypes: readonly string[];
}): boolean {
  return mimeTypes.some((mimeType) =>
    mimeType.endsWith('/*')
      ? candidate.type.startsWith(mimeType.slice(0, -1))
      : candidate.type === mimeType,
  );
}

function mimeTypesMessage(mimeTypes: readonly string[]): string {
  return `Only the following file types are allowed: ${mimeTypes.join(', ')}`;
}

type UploadedFileOptions = {
  mimeTypes?: readonly string[];
  maxFileSizeBytes?: number;
};

function validateUploadedFile({
  candidate,
  options,
  context,
}: {
  candidate: UploadedFileLike;
  options: UploadedFileOptions;
  context: z.RefinementCtx;
}): void {
  if (options.mimeTypes?.length) {
    if (!matchesMimeTypes({ candidate, mimeTypes: options.mimeTypes })) {
      context.addIssue({
        code: 'custom',
        message: mimeTypesMessage(options.mimeTypes),
      });
    }
  }
  if (
    options.maxFileSizeBytes !== undefined &&
    candidate.size > options.maxFileSizeBytes
  ) {
    context.addIssue({
      code: 'custom',
      message: `File "${candidate.name}" exceeds the maximum allowed size of ${options.maxFileSizeBytes} bytes`,
    });
  }
}

/** Zod v4 `z.file()` — OpenAPI via `@orpc/zod/zod4` without v3 `setCustomZodDef`. */
function createOrpcFileSchema() {
  return z.file({
    error: 'Input is not a file',
  });
}

export function uploadedFileSchema(options: UploadedFileOptions = {}) {
  return createOrpcFileSchema().superRefine((candidate, context) => {
    validateUploadedFile({ candidate, options, context });
  });
}

export function uploadedFilesSchema({
  maxFiles,
  ...options
}: UploadedFileOptions & { maxFiles?: number } = {}) {
  const single = createOrpcFileSchema();
  return z
    .union([z.array(single), single])
    .transform((value) => (Array.isArray(value) ? value : [value]))
    .superRefine((files, context) => {
      if (maxFiles !== undefined && files.length > maxFiles) {
        context.addIssue({
          code: 'custom',
          message: `At most ${maxFiles} files are allowed`,
        });
      }
      for (const candidate of files) {
        validateUploadedFile({ candidate, options, context });
      }
    });
}
